// OSIS - Vânzări & CRM Dashboard
import React, { useState } from 'react';
import { 
  TrendingUp, 
  Users, 
  DollarSign, 
  Target, 
  Calendar, 
  Phone, 
  Mail, 
  MapPin,
  Briefcase,
  Award,
  AlertCircle,
  CheckCircle,
  Clock,
  Building,
  Home,
  Calculator
} from 'lucide-react';
import Card from '../../components/Card';
import Table from '../../components/Table';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';

const SalesDashboard = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('luna');
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);

  // KPI-uri vânzări OSIS
  const salesKPIs = {
    vanzariLuna: {
      value: '127,850 RON',
      change: '+18.5%',
      trend: 'up',
      description: 'Vânzări Luna Curentă'
    },
    leaduriActive: {
      value: '23',
      change: '+5',
      trend: 'up',
      description: 'Leaduri Active în Pipeline'
    },
    rataConversie: {
      value: '32.5%',
      change: '+4.2%',
      trend: 'up',
      description: 'Rata de Conversie'
    },
    valoareMedie: {
      value: '5,560 RON',
      change: '+8.1%',
      trend: 'up',
      description: 'Valoare Medie Comandă'
    }
  };

  // Pipeline-ul de vânzări pentru pardoseli industriale
  const salesPipeline = [
    {
      id: 1,
      client: 'Popescu Maria',
      company: 'Particular',
      tip: 'B2C',
      produs: '6 proiecte epoxidic + lucrare intrare',
      valoare: '18,500 RON',
      stadiu: 'Măsurătoare programată',
      probabilitate: 75,
      dataContact: '2024-09-02',
      responsabil: 'Ciprian Aostăcioaei',
      telefon: '+40 722 123 456',
      adresa: 'Str. Florilor 25, Suceava',
      note: 'Clasa energetică A+, geamuri triple',
      ultimaActivitate: '2024-09-04'
    },
    {
      id: 2,
      client: 'SRL Construct Alpha',
      company: 'Construct Alpha SRL',
      tip: 'B2B',
      produs: '24 proiecte bloc + lucrări balcon',
      valoare: '45,200 RON',
      stadiu: 'Oferta trimisă',
      probabilitate: 60,
      dataContact: '2024-08-28',
      responsabil: 'Ionel Filip',
      telefon: '+40 721 987 654',
      adresa: 'Bd. Eroilor 15, Suceava',
      note: 'Proiect rezidențial, 3 etaje',
      ultimaActivitate: '2024-09-03'
    },
    {
      id: 3,
      client: 'Georgescu Ion',
      company: 'Particular',
      tip: 'B2C',
      produs: '8 proiecte + lucrare garaj',
      valoare: '12,800 RON',
      stadiu: 'Negociere preț',
      probabilitate: 85,
      dataContact: '2024-08-30',
      responsabil: 'Ciprian Aostăcioaei',
      telefon: '+40 723 456 789',
      adresa: 'Str. Mihai Viteazu 8, Râșnov',
      note: 'Client recurent, reducere 5%',
      ultimaActivitate: '2024-09-04'
    },
    {
      id: 4,
      client: 'Hotel Mountain View',
      company: 'Mountain View SRL',
      tip: 'B2B',
      produs: '36 proiecte + lucrări terasă',
      valoare: '78,300 RON',
      stadiu: 'Prima întâlnire',
      probabilitate: 40,
      dataContact: '2024-09-01',
      responsabil: 'Ionel Filip',
      telefon: '+40 724 567 890',
      adresa: 'Str. Postavaru 12, Predeal',
      note: 'Renovare completă hotel',
      ultimaActivitate: '2024-09-02'
    },
    {
      id: 5,
      client: 'Marinescu Ana',
      company: 'Particular',
      tip: 'B2C',
      produs: '4 proiecte mansardă',
      valoare: '9,600 RON',
      stadiu: 'Contract semnat',
      probabilitate: 95,
      dataContact: '2024-08-25',
      responsabil: 'Ciprian Aostăcioaei',
      telefon: '+40 722 678 901',
      adresa: 'Str. Dealul Mare 33, Suceava',
      note: 'Producție programată săpt. viitoare',
      ultimaActivitate: '2024-09-05'
    }
  ];

  // Statistici pe produse OSIS
  const produseStatistici = [
    { produs: 'proiecte epoxidic', vanzari: 145, valoare: '89,500 RON', procent: 45 },
    { produs: 'lucrări epoxidic', vanzari: 87, valoare: '52,200 RON', procent: 28 },
    { produs: 'proiecte Aluminiu', vanzari: 34, valoare: '31,800 RON', procent: 16 },
    { produs: 'lucrări Blindate', vanzari: 23, valoare: '27,600 RON', procent: 11 }
  ];

  const getStadiuColor = (stadiu) => {
    const colors = {
      'Prima întâlnire': 'gray',
      'Măsurătoare programată': 'blue',
      'Oferta trimisă': 'yellow',
      'Negociere preț': 'orange',
      'Contract semnat': 'green'
    };
    return colors[stadiu] || 'gray';
  };

  const getTipIcon = (tip) => {
    return tip === 'B2B' ? <Briefcase className="w-4 h-4" /> : <Home className="w-4 h-4" />;
  };

  const handleLeadClick = (lead) => {
    setSelectedLead(lead);
    setShowLeadModal(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Vânzări & CRM OSIS
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Pipeline vânzări pardoseli industriale - {new Date().toLocaleDateString('ro-RO', { month: 'long', year: 'numeric' })}
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3 mt-4 sm:mt-0">
            <select 
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 dark:text-white"
            >
              <option value="saptamana">Această Săptămână</option>
              <option value="luna">Această Lună</option>
              <option value="trimestru">Acest Trimestru</option>
            </select>
            
            <button className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
              <Calculator className="w-4 h-4 mr-2" />
              Calculator Preț
            </button>
          </div>
        </div>

        {/* KPI-uri */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {Object.entries(salesKPIs).map(([key, kpi]) => (
            <Card key={key} className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center justify-center w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-xl">
                    {key === 'vanzariLuna' && <DollarSign className="w-6 h-6 text-primary-600" />}
                    {key === 'leaduriActive' && <Users className="w-6 h-6 text-primary-600" />}
                    {key === 'rataConversie' && <Target className="w-6 h-6 text-primary-600" />}
                    {key === 'valoareMedie' && <TrendingUp className="w-6 h-6 text-primary-600" />}
                  </div>
                </div>
                <Badge 
                  variant={kpi.trend === 'up' ? 'success' : 'warning'}
                  className="text-xs"
                >
                  {kpi.change}
                </Badge>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                  {kpi.value}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  {kpi.description}
                </p>
              </div>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Pipeline Principal */}
          <div className="lg:col-span-2">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Pipeline Vânzări Active
                </h2>
                <Badge variant="primary" className="px-3 py-1">
                  {salesPipeline.length} leaduri active
                </Badge>
              </div>

              <div className="space-y-4">
                {salesPipeline.map((lead) => (
                  <div 
                    key={lead.id}
                    onClick={() => handleLeadClick(lead)}
                    className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-all cursor-pointer"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center justify-center w-10 h-10 bg-gray-100 dark:bg-gray-800 rounded-lg">
                          {getTipIcon(lead.tip)}
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-white">
                            {lead.client}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {lead.company !== 'Particular' ? lead.company : 'Client particular'}
                          </p>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <p className="font-bold text-gray-900 dark:text-white">
                          {lead.valoare}
                        </p>
                        <Badge variant={getStadiuColor(lead.stadiu)} className="text-xs mt-1">
                          {lead.stadiu}
                        </Badge>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                      <span>{lead.produs}</span>
                      <div className="flex items-center space-x-4">
                        <span className="flex items-center">
                          <Target className="w-4 h-4 mr-1" />
                          {lead.probabilitate}%
                        </span>
                        <span className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          {new Date(lead.ultimaActivitate).toLocaleDateString('ro-RO')}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Statistici Produse */}
          <div className="space-y-6">
            <Card className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                Top Produse
              </h2>
              
              <div className="space-y-4">
                {produseStatistici.map((produs, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {produs.produs}
                        </span>
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {produs.valoare}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-gray-500">
                          {produs.vanzari} bucăți
                        </span>
                        <span className="text-xs text-gray-500">
                          {produs.procent}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-primary-500 to-secondary-500 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${produs.procent}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Activități Recente */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                Activități Recente
              </h2>
              
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="flex items-center justify-center w-8 h-8 bg-green-100 dark:bg-green-900 rounded-full">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-900 dark:text-white">
                      Contract semnat cu <strong>Marinescu Ana</strong>
                    </p>
                    <p className="text-xs text-gray-500">Azi, 14:30</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="flex items-center justify-center w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full">
                    <Phone className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-900 dark:text-white">
                      Apel cu <strong>Georgescu Ion</strong>
                    </p>
                    <p className="text-xs text-gray-500">Ieri, 16:15</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="flex items-center justify-center w-8 h-8 bg-yellow-100 dark:bg-yellow-900 rounded-full">
                    <Mail className="w-4 h-4 text-yellow-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-900 dark:text-white">
                      Ofertă trimisă la <strong>Construct Alpha</strong>
                    </p>
                    <p className="text-xs text-gray-500">03.09, 11:20</p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Modal Detalii Lead */}
      <Modal isOpen={showLeadModal} onClose={() => setShowLeadModal(false)}>
        {selectedLead && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                {getTipIcon(selectedLead.tip)}
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {selectedLead.client}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400">
                    {selectedLead.company}
                  </p>
                </div>
              </div>
              <Badge variant={getStadiuColor(selectedLead.stadiu)}>
                {selectedLead.stadiu}
              </Badge>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-gray-400" />
                  <span className="text-gray-900 dark:text-white">{selectedLead.telefon}</span>
                </div>
                
                <div className="flex items-center space-x-3">
                  <MapPin className="w-5 h-5 text-gray-400" />
                  <span className="text-gray-900 dark:text-white">{selectedLead.adresa}</span>
                </div>
                
                <div className="flex items-center space-x-3">
                  <Award className="w-5 h-5 text-gray-400" />
                  <span className="text-gray-900 dark:text-white">Responsabil: {selectedLead.responsabil}</span>
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Detalii Comandă</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    {selectedLead.produs}
                  </p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {selectedLead.valoare}
                  </p>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-primary-50 dark:bg-primary-900/30 rounded-lg">
                  <span className="text-primary-700 dark:text-primary-300 font-medium">
                    Probabilitate
                  </span>
                  <span className="text-primary-600 font-bold text-lg">
                    {selectedLead.probabilitate}%
                  </span>
                </div>
              </div>
            </div>

            {selectedLead.note && (
              <div className="bg-yellow-50 dark:bg-yellow-900/30 p-4 rounded-lg mb-6">
                <h4 className="font-semibold text-yellow-800 dark:text-yellow-200 mb-2">
                  Note importante
                </h4>
                <p className="text-yellow-700 dark:text-yellow-300">
                  {selectedLead.note}
                </p>
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                Programează Întâlnire
              </button>
              <button className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
                Actualizează Status
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SalesDashboard;